import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import type { FeedingPen } from '@/domain/feedingDashboard';

interface FeedingServiceNoticeProps {
    pens: FeedingPen[];
    /** Display name per pen id, unique within the period. */
    names: Map<number, string>;
    /** The planning system's name as the labels show it. */
    system: string;
    className?: string;
}

/**
 * Names the pens the feeding data service did not answer for.
 *
 * The call fails soft, so such a pen still renders, but its planning-system values, bFCR
 * bioscope and Bioscope trend are gone and its cells read like an unmapped signal or a
 * day without data. This is where the real reason is stated. It says "feeding data
 * service" on purpose: the failure is the call to service-feeding-rest, which absorbs a
 * planning-system outage itself.
 *
 * A failed call is a fault, so it takes the portal's danger alert: the warning tone is
 * kept for the four warning triggers (O7), and the info tone for expected states such as
 * pens still collecting data.
 */
export function FeedingServiceNotice(props: FeedingServiceNoticeProps) {
    const { pens, names, system, className } = props;
    const { t } = useTranslation();
    const failed = pens.filter((pen) => pen.feedingServiceUnavailable);
    if (failed.length === 0) return null;

    const text =
        failed.length === pens.length
            ? t('feedingModule.feedingServiceUnavailableAll', { system })
            : t('feedingModule.feedingServiceUnavailable', {
                  count: failed.length,
                  pens: failed.map((pen) => names.get(pen.cageId) ?? pen.cageName).join(', '),
                  system,
              });
    // The span is the live region, so the Alert itself stays unannounced.
    return (
        <div className={className}>
            <Alert tone="danger" size="small" live="off">
                <span role="status">{text}</span>
            </Alert>
        </div>
    );
}
